import { useMemo, useState, type ReactElement } from 'react';
import type { Floorplan } from '@vibesites/schema';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema, ComponentDesignSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { getLiveDataUrl } from '@vibesites/runtime';
import {
  EditableBlockToken,
  EditableCollectionBinding,
  useBlockCollectionBindingState,
  useBlockSlotGroup,
  useBlockToken,
  useFees,
  useProperty,
  type SlotGroupResult,
} from '@vibesites/runtime/react';
import { defineComponentRequirements, collections } from '@vibesites/tokens';
import { NO_FILTERS, toContent, type ApplyLink, type BedChoice, type Contact, type EditableText, type FloorplansLayout, type IsoDate, type PlanFilters, type PlanSort } from './model';
import { DefaultFloorplansView } from './view';
import { isoDate, text } from './wire';

function FloorplansTitle({ as = 'h2', className }: { as?: 'h2' | 'h3' | 'p' | 'span'; className?: string }) {
  const title = useBlockToken('title', { defaultValue: '' });
  return (
    <EditableBlockToken localName="title" type="text" label="Section Title" defaultValue="" as={as} className={className}>
      {title}
    </EditableBlockToken>
  );
}

function FloorplansSubtitle({ as = 'p', className }: { as?: 'h2' | 'h3' | 'p' | 'span'; className?: string }) {
  const subtitle = useBlockToken('subtitle', { defaultValue: '' });
  return (
    <EditableBlockToken localName="subtitle" type="text" label="Section Subtitle" defaultValue="" as={as} className={className}>
      {subtitle}
    </EditableBlockToken>
  );
}

const Title: EditableText = FloorplansTitle;
const Subtitle: EditableText = FloorplansSubtitle;

function MultihubFloorplansBlock(_props: BlockProps): ReactElement {
  const plans = useBlockCollectionBindingState<Floorplan>('plans', { collection: 'floorplans', liveCollection: 'floorplans_full' });
  const title = useBlockToken('title', { defaultValue: '' });
  const subtitle = useBlockToken('subtitle', { defaultValue: '' });
  const apply = useBlockSlotGroup('apply', APPLY_LINK);
  const contact = useBlockSlotGroup('contact', CONTACT_LINK);
  const propertyFees = useFees();
  const property = useProperty();
  const [beds, setBeds] = useState<BedChoice>(null);
  /** Tablet and up. Phones always get cards, which the view enforces in CSS. */
  const [layout, setLayout] = useState<FloorplansLayout>('rows');
  const [sort, setSort] = useState<PlanSort>('available');
  const [filters, setFilters] = useState<PlanFilters>(NO_FILTERS);
  const content = useMemo(() => toContent(
    plans,
    {
      today: todayIso(),
      apply: applyFor(apply),
      contact: contactFor(contact, property.property_phone),
      propertyFees,
      propertyPets: property.pet_policies ?? [],
    },
    { selected: beds, select: setBeds },
    { selected: sort, select: setSort },
    { selected: filters, select: setFilters },
  ), [plans, apply, contact, propertyFees, property.pet_policies, property.property_phone, beds, sort, filters]);
  const header = title ? { Title, Subtitle: subtitle ? Subtitle : null } : null;
  /** In the prerendered HTML, so the browser opens the connection while the bundle loads. */
  const liveOrigin = liveDataOrigin();
  return (
    <EditableCollectionBinding bindingPath={plans.bindingPath} label="Floor plans">
      {liveOrigin && <link rel="preconnect" href={liveOrigin} crossOrigin="anonymous" />}
      <DefaultFloorplansView header={header} layout={{ selected: layout, select: setLayout }} content={content} />
    </EditableCollectionBinding>
  );
}

function liveDataOrigin(): string | null {
  const url = getLiveDataUrl();
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

/**
 * The runtime definitions of the two link groups. `contentSchema.slotGroups` repeats them inline,
 * because the schema linter evaluates the schema without references. A test keeps the two equal.
 */
export const APPLY_LINK: SlotGroupDefinition = {
  label: 'Apply link',
  description: 'The apply button on a plan or home that sends no apply link of its own.',
  fields: {
    url: { label: 'Link', type: 'url', defaultField: 'property_apply_now_url' },
    label: { label: 'Title', type: 'text', defaultValue: 'Apply now' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self', description: 'How to open the link: _self (same tab) or _blank (new tab).' },
  },
};

export const CONTACT_LINK: SlotGroupDefinition = {
  label: 'Contact link',
  description: 'The contact button and the closing prompt. An empty link calls the property phone.',
  fields: {
    url: { label: 'Link', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Title', type: 'text', defaultValue: 'Contact us' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self', description: 'How to open the link: _self (same tab) or _blank (new tab).' },
  },
};

const APPLY_LABEL = 'Apply now';
const CONTACT_LABEL = 'Contact us';

function applyFor(link: SlotGroupResult): ApplyLink {
  return { href: text(link.url), label: text(link.label) ?? APPLY_LABEL, newTab: link._target === '_blank' };
}

function contactFor(link: SlotGroupResult, phone: string | null | undefined): Contact | null {
  const dial = text(phone);
  const href = text(link.url) ?? (dial ? `tel:${dial}` : null);
  if (!href) return null;
  return { href, label: text(link.label) ?? CONTACT_LABEL, newTab: link._target === '_blank' };
}

function todayIso(): IsoDate {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const parsed = isoDate(`${now.getFullYear()}-${month}-${day}`);
  if (!parsed) throw new Error('today');
  return parsed;
}

const contentSchema: ComponentContentSchema = {
  slot: 'MultihubFloorplans',
  displayName: 'Floor Plans',
  tokens: {
    title: {
      label: 'Section Title',
      type: 'text',
      defaultValue: '',
      description: 'The main headline for the floor plans section.',
    },
    subtitle: {
      label: 'Section Subtitle',
      type: 'text',
      defaultValue: '',
      description: 'Optional subtitle/label below the title.',
    },
  },
  slotGroups: {
    apply: {
      label: 'Apply link',
      description: 'The apply button on a plan or home that sends no apply link of its own.',
      fields: {
        url: { label: 'Link', type: 'url', defaultField: 'property_apply_now_url' },
        label: { label: 'Title', type: 'text', defaultValue: 'Apply now' },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_self', description: 'How to open the link: _self (same tab) or _blank (new tab).' },
      },
    },
    contact: {
      label: 'Contact link',
      description: 'The contact button and the closing prompt. An empty link calls the property phone.',
      fields: {
        url: { label: 'Link', type: 'url', defaultValue: '/contact-us' },
        label: { label: 'Title', type: 'text', defaultValue: 'Contact us' },
        _target: { label: 'Link Target', type: 'text', defaultValue: '_self', description: 'How to open the link: _self (same tab) or _blank (new tab).' },
      },
    },
  },
  collectionBindings: {
    plans: { label: 'Floor plans', collection: 'floorplans' },
  },
};

const designSchema: ComponentDesignSchema = {
  slot: 'MultihubFloorplans',
  displayName: 'Floor Plans',
  tokens: {
    'color.background': { label: 'Background', type: 'color', cssVariable: '--vibe-color-background', description: 'Section background behind the plans.' },
    'color.text': { label: 'Text', type: 'color', cssVariable: '--vibe-color-text' },
    'color.textMuted': { label: 'Muted text', type: 'color', cssVariable: '--vibe-color-text-muted' },
    'color.border': { label: 'Border', type: 'color', cssVariable: '--vibe-color-border' },
    'color.primary': { label: 'Primary', type: 'color', cssVariable: '--vibe-color-primary', description: 'Apply buttons, the selected bedroom filter, and the selected home.' },
    'color.textOnPrimary': { label: 'Text on primary', type: 'color', cssVariable: '--vibe-color-text-on-primary' },
    'color.secondary': { label: 'Secondary', type: 'color', cssVariable: '--vibe-color-secondary', description: 'Special offer chips, expand buttons, and the contact button.' },
    'color.textOnSecondary': { label: 'Text on secondary', type: 'color', cssVariable: '--vibe-color-text-on-secondary' },
    'color.success': { label: 'Available', type: 'color', cssVariable: '--vibe-color-success', description: 'Availability dots.' },
    'font.heading': { label: 'Heading', type: 'font', cssVariable: '--vibe-font-heading' },
  },
};

export const multihubFloorplansBlock: BlockDefinition = {
  id: 'multihub-floorplans',
  name: 'Multihub Floorplans',
  description: 'Floor plans with listed units, rent, specials, fees, and apply links.',
  category: 'Content',
  component: MultihubFloorplansBlock,
  contentSchema,
  designSchema,
  requirements: defineComponentRequirements('MultihubFloorplans', {
    designTokens: ['color.background', 'color.text', 'color.textMuted', 'color.border', 'color.primary', 'color.textOnPrimary', 'color.secondary', 'color.textOnSecondary', 'font.heading'],
    dataTokens: [collections.required('floorplans', 'Floor plans')],
  }),
  tags: ['floorplans', 'units', 'pricing', 'availability'],
  defaultGridSpan: 12,
};
